// transformed by jscodeshift
import type { Map } from '@gonfalon/immutable-shim/immutableShim';
import { fromJS, List } from '@gonfalon/immutable-shim/immutableShim';
import { http, jsonToImmutable, jsonToImmutableError } from '@gonfalon/legacy-http-client/httpUtils';
import type { SegmentSemanticInstruction } from '@gonfalon/openapi/types';
import { normalize, schema } from 'normalizr';

import type { FetchSegmentsResponse } from '#actions/segments';
import { createJsonPatch } from '#utils/patchUtils';
import type { BigSegmentUploadMode } from '#utils/segment/constants';
import type { BigSegmentIndividualTargetUpdates } from '#utils/segment/records/BigSegmentIndividualTargetUpdates';
import { createBigSegmentTarget } from '#utils/segment/records/BigSegmentTarget';
import type { Segment } from '#utils/segment/records/Segment';
import { createSegment as createSegmentRecord } from '#utils/segment/records/Segment';
import { serializeSegmentForAPI } from '#utils/segment/serializeSegmentForAPI';

const segmentEntity = new schema.Entity(
  'segments',
  {},
  {
    idAttribute: 'key',
  },
);

export const normalizeGetSegmentsResponse = (jsonRes: unknown): FetchSegmentsResponse => {
  const data = normalize(jsonRes, { items: [segmentEntity] });
  if (data.result.items.length === 0) {
    return fromJS(data).setIn(['entities', 'segments'], List());
  }
  return fromJS(data).updateIn(['entities', 'segments'], (ss: Map<string, Segment>) =>
    ss
      .map(createSegmentRecord)
      // the 'normalizr' package orders items in order, but we
      // want to keep the order returned from the API. This is important for sorting/filtering.
      .sort((a, b) => data.result.items.indexOf(a.key) - data.result.items.indexOf(b.key)),
  );
};

export const getSegments = (projKey: string, envKey: string, queryString?: string, signal?: AbortSignal) => {
  let url = `/api/v2/segments/${projKey}/${envKey}`;
  if (queryString) {
    url += `?${queryString}&expand=flags`;
  } else {
    url += '?expand=flags';
  }

  return http
    .get(url, { signal })
    .then((res) => res.json().then((responseJson) => normalizeGetSegmentsResponse(responseJson)))
    .catch(jsonToImmutableError);
};

export const createSegment = (projKey: string, envKey: string, segment: Segment) =>
  http
    .post(`/api/v2/segments/${projKey}/${envKey}`, {
      body: serializeSegmentForAPI(segment),
    })
    .then(jsonToImmutable)
    .then(createSegmentRecord)
    .catch(jsonToImmutableError);

export const deleteSegment = (segment: Segment) => http.delete(segment.selfLink()).catch(jsonToImmutableError);

export const getSegmentByKey = (projKey: string, envKey: string, key: string) =>
  http
    .get(`/api/v2/segments/${projKey}/${envKey}/${key}`)
    .then(jsonToImmutable)
    .then(createSegmentRecord)
    .catch(jsonToImmutableError);

export const updateSegment = (oldSegment: Segment, newSegment: Segment, comment = '') => {
  const patch = createJsonPatch(oldSegment, newSegment, { comment, shouldTestVersion: true });
  return http
    .patch(oldSegment.selfLink(), {
      body: patch,
    })
    .then(jsonToImmutable)
    .then(createSegmentRecord)
    .catch(jsonToImmutableError);
};

export const updateSegmentSemantically = (
  oldSegment: Segment,
  input: { comment?: string; environmentKey?: string; instructions: SegmentSemanticInstruction[] },
) =>
  http
    .patch(oldSegment.selfLink(), {
      headers: {
        'Content-Type': 'application/json; domain-model=launchdarkly.semanticpatch',
      },
      body: input,
    })
    .then(jsonToImmutable)
    .then(createSegmentRecord)
    .catch(jsonToImmutableError);

export const uploadCsvToSegment = (
  projKey: string,
  envKey: string,
  key: string,
  file: File,
  uploadMode: BigSegmentUploadMode,
  waitOnApprovals?: boolean,
) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('mode', uploadMode);

  if (waitOnApprovals) {
    formData.append('waitOnApprovals', String(waitOnApprovals));
  }

  return http
    .post(`/api/v2/segments/${projKey}/${envKey}/${key}/imports`, {
      body: formData,
      headers: { 'LD-API-Version': 'beta' },
    })
    .catch(jsonToImmutableError);
};

export const getBigSegmentTarget = (segment: Segment, userKey: string) =>
  http
    .get(`${segment.selfLink()}/users/${encodeURIComponent(userKey)}`, { beta: true })
    .then(jsonToImmutable)
    .then(createBigSegmentTarget)
    .catch(jsonToImmutableError);

export const getBigSegmentFileMetaData = (projKey: string, envKey: string, segmentKey: string, exportKey: string) =>
  http
    .get(`/api/v2/segments/${projKey}/${envKey}/${segmentKey}/exports/${exportKey}`, {
      headers: { 'LD-API-Version': 'beta' },
    })
    .then(jsonToImmutable)
    .catch(jsonToImmutableError);

export const postSegmentExport = (projKey: string, envKey: string, segmentKey: string) =>
  http
    .post(`/api/v2/segments/${projKey}/${envKey}/${segmentKey}/exports`, {
      headers: { 'LD-API-Version': 'beta' },
    })
    .catch(jsonToImmutableError);

export const updateBigSegmentIndividualTargets = ({
  projKey,
  envKey,
  segmentKey,
  updates,
}: {
  projKey: string;
  envKey: string;
  segmentKey: string;
  updates: BigSegmentIndividualTargetUpdates;
}) =>
  http
    .post(`/api/v2/segments/${projKey}/${envKey}/${segmentKey}/users`, {
      body: updates.toRep(),
    })
    .catch(jsonToImmutableError);
